import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useState, useEffect, useRef } from 'react';
import { VideoCallWrapper } from '../components/video/VideoCallInterface';
import { Button } from '../components/ui/button';
import toast from 'react-hot-toast';

// Type definitions for Apollo queries/mutations
interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface GetCurrentUserData {
  getCurrentUser: CurrentUser;
}

interface VideoConference {
  id: string;
  title: string;
  status: string;
  scheduledStartTime: string;
  scheduledEndTime: string;
  hostId: string;
  host?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  attendeeIds?: string[];
  guestName?: string;
  guestEmail?: string;
  isRecordingEnabled: boolean;
  recordingStatus?: string;
  chimeMeetingId?: string;
  waitingRoomEnabled?: boolean;
  waitingRoomParticipants?: Array<{
    id: string;
    name: string;
    participantType: string;
    requestedAt: string;
  }>;
}

interface GetVideoConferenceData {
  getVideoConference: VideoConference;
}

interface MeetingCredentials {
  meetingId: string;
  mediaRegion: string;
  meetingHostId?: string;
  attendeeId: string;
  joinToken: string;
  externalUserId: string;
  meetingJson: string;
  attendeeJson: string;
  status?: string;
  waitingParticipantId?: string;
  message?: string;
}

interface StartVideoConferenceData {
  startVideoConference: MeetingCredentials;
}

interface JoinVideoConferenceData {
  joinVideoConference: MeetingCredentials;
}

interface EndVideoConferenceData {
  endVideoConference: {
    id: string;
    status: string;
  };
}

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
    }
  }
`;

const GET_VIDEO_CONFERENCE = gql`
  query GetVideoConference($id: ID!) {
    getVideoConference(id: $id) {
      id
      title
      status
      scheduledStartTime
      scheduledEndTime
      hostId
      host {
        id
        firstName
        lastName
      }
      attendeeIds
      guestName
      guestEmail
      isRecordingEnabled
      recordingStatus
      chimeMeetingId
      waitingRoomEnabled
      waitingRoomParticipants {
        id
        name
        participantType
        requestedAt
      }
    }
  }
`;

const START_VIDEO_CONFERENCE = gql`
  mutation StartVideoConference($id: ID!) {
    startVideoConference(id: $id) {
      meetingId
      mediaRegion
      meetingHostId
      attendeeId
      joinToken
      externalUserId
      meetingJson
      attendeeJson
    }
  }
`;

const JOIN_VIDEO_CONFERENCE = gql`
  mutation JoinVideoConference($id: ID!) {
    joinVideoConference(id: $id) {
      meetingId
      mediaRegion
      meetingHostId
      attendeeId
      joinToken
      externalUserId
      meetingJson
      attendeeJson
      status
      waitingParticipantId
      message
    }
  }
`;

const END_VIDEO_CONFERENCE = gql`
  mutation EndVideoConference($id: ID!) {
    endVideoConference(id: $id) {
      id
      status
    }
  }
`;

export function VideoCall() {
  const { conferenceId } = useParams<{ conferenceId: string }>();
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState<any>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [callEnded, setCallEnded] = useState(false);
  const [isInWaitingRoom, setIsInWaitingRoom] = useState(false);
  const [waitingParticipantId, setWaitingParticipantId] = useState<string | null>(null);
  const [didStartMeeting, setDidStartMeeting] = useState(false);
  
  // Pre-meeting lobby state
  const [showLobby, setShowLobby] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>('');
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>('');
  const videoPreviewRef = useRef<HTMLVideoElement>(null);

  const { data: userData } = useQuery<GetCurrentUserData>(GET_CURRENT_USER);
  const user = userData?.getCurrentUser;

  const { data, loading, error, refetch } = useQuery<GetVideoConferenceData>(GET_VIDEO_CONFERENCE, {
    variables: { id: conferenceId },
    skip: !conferenceId,
    pollInterval: isInWaitingRoom ? 3000 : 0,
  });

  const [startConference] = useMutation<StartVideoConferenceData>(START_VIDEO_CONFERENCE);
  const [joinConference] = useMutation<JoinVideoConferenceData>(JOIN_VIDEO_CONFERENCE);
  const [endConference] = useMutation<EndVideoConferenceData>(END_VIDEO_CONFERENCE);

  const conference = data?.getVideoConference;
  const isHost = conference?.hostId === user?.id;
  const isStaffAttendee = user && conference?.attendeeIds?.includes(user.id) && 
    ['ADMIN', 'ATTORNEY', 'PARALEGAL', 'STAFF'].includes(user.role);
  const canStartMeeting = isHost || isStaffAttendee || user?.role === 'ADMIN';

  // Initialize media devices for lobby preview
  useEffect(() => {
    if (!showLobby) return;

    const initializeDevices = async () => {
      try {
        // Request permissions and get devices
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: true, 
          audio: true 
        });
        setLocalStream(stream);

        // Get device list
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        const videoInputs = devices.filter(d => d.kind === 'videoinput');
        
        setAudioDevices(audioInputs);
        setVideoDevices(videoInputs);
        
        if (audioInputs.length > 0) setSelectedAudioDevice(audioInputs[0].deviceId);
        if (videoInputs.length > 0) setSelectedVideoDevice(videoInputs[0].deviceId);

        // Attach stream to video preview
        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error('Error accessing media devices:', err);
        // Still allow joining without preview
      }
    };

    initializeDevices();

    return () => {
      // Cleanup stream when leaving lobby
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [showLobby]);

  // Update video preview when camera is toggled
  useEffect(() => {
    if (localStream) {
      localStream.getVideoTracks().forEach(track => {
        track.enabled = isCameraOn;
      });
    }
  }, [isCameraOn, localStream]);

  // Update audio when mic is toggled
  useEffect(() => {
    if (localStream) {
      localStream.getAudioTracks().forEach(track => {
        track.enabled = isMicOn;
      });
    }
  }, [isMicOn, localStream]);

  // Handle device change
  const handleDeviceChange = async (type: 'audio' | 'video', deviceId: string) => {
    if (type === 'audio') {
      setSelectedAudioDevice(deviceId);
    } else {
      setSelectedVideoDevice(deviceId);
    }

    // Update stream with new device
    try {
      const constraints: MediaStreamConstraints = {
        audio: type === 'audio' ? { deviceId: { exact: deviceId } } : (selectedAudioDevice ? { deviceId: { exact: selectedAudioDevice } } : true),
        video: type === 'video' ? { deviceId: { exact: deviceId } } : (selectedVideoDevice ? { deviceId: { exact: selectedVideoDevice } } : true),
      };

      // Stop old stream
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }

      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      setLocalStream(newStream);

      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = newStream;
      }
    } catch (err) {
      console.error('Error switching device:', err);
    }
  };

  // Start/Join meeting from lobby
  const handleStartFromLobby = async () => {
    // Stop preview stream before joining
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }
    setShowLobby(false);
  };

  // Auto-connect when leaving lobby
  useEffect(() => {
    if (!conference || credentials || isConnecting || showLobby) return;
    // Don't try to connect if already in waiting room
    if (isInWaitingRoom) return;

    const connect = async () => {
      setIsConnecting(true);
      try {
        let result;
        let mutationResult;
        
        if (conference.status === 'SCHEDULED') {
          // Start the meeting if scheduled (only host/staff can do this)
          mutationResult = await startConference({ variables: { id: conferenceId } });
          if (mutationResult.error) {
            throw new Error(mutationResult.error.message);
          }
          result = mutationResult.data?.startVideoConference;
          setDidStartMeeting(true);
        } else if (conference.status === 'IN_PROGRESS') {
          // Join existing meeting
          mutationResult = await joinConference({ variables: { id: conferenceId } });
          if (mutationResult.error) {
            throw new Error(mutationResult.error.message);
          }
          result = mutationResult.data?.joinVideoConference;
        } else {
          throw new Error(`Cannot join conference with status: ${conference.status}`);
        }

        if (!result) {
          throw new Error('No credentials returned from server');
        }

        // Check if user is in waiting room
        if (result.status === 'WAITING') {
          setIsInWaitingRoom(true);
          setWaitingParticipantId(result.waitingParticipantId || null);
          toast(result.message || 'Please wait for the host to admit you', { icon: '⏳' });
          return;
        }

        // Build credentials for Chime SDK
        const meeting = result.meetingJson ? JSON.parse(result.meetingJson) : null;
        const attendee = result.attendeeJson ? JSON.parse(result.attendeeJson) : null;
        
        if (!meeting || !attendee) {
          throw new Error('Invalid meeting credentials received - missing meeting or attendee data');
        }

        const creds = {
          meetingId: result.meetingId,
          mediaRegion: result.mediaRegion,
          attendeeId: result.attendeeId,
          joinToken: result.joinToken,
          externalUserId: result.externalUserId,
          meeting,
          attendee,
        };

        setCredentials(creds);
        await refetch();
      } catch (err: any) {
        console.error('Error connecting to meeting:', err);
        toast.error(err.message || 'Failed to connect to meeting');
        navigate('/video-conferences');
      } finally {
        setIsConnecting(false);
      }
    };

    connect();
  }, [conference, credentials, isConnecting, isInWaitingRoom, showLobby, conferenceId, startConference, joinConference, refetch, navigate]);

  // Check if user has been admitted from waiting room
  useEffect(() => {
    if (!isInWaitingRoom || !waitingParticipantId || credentials) return;

    const checkAdmission = async () => {
      try {
        // Try to join again - if admitted, we'll get credentials
        const mutationResult = await joinConference({ variables: { id: conferenceId } });
        const result = mutationResult.data?.joinVideoConference;
        
        if (!result) return;
        
        // Still waiting
        if (result.status === 'WAITING') {
          return;
        }
        
        // Got credentials - we've been admitted!
        const meeting = result.meetingJson ? JSON.parse(result.meetingJson) : null;
        const attendee = result.attendeeJson ? JSON.parse(result.attendeeJson) : null;
        
        if (meeting && attendee) {
          toast.success('You have been admitted to the meeting!');
          setIsInWaitingRoom(false);
          setCredentials({
            meetingId: result.meetingId,
            mediaRegion: result.mediaRegion,
            attendeeId: result.attendeeId,
            joinToken: result.joinToken,
            externalUserId: result.externalUserId,
            meeting,
            attendee,
          });
        }
      } catch (err: any) {
        // If conference ended or was cancelled, redirect
        if (err.message?.includes('cancelled') || err.message?.includes('ended')) {
          toast.error(err.message);
          navigate('/video-conferences');
        }
      }
    };

    // Check every 3 seconds
    const interval = setInterval(checkAdmission, 3000);
    return () => clearInterval(interval);
  }, [isInWaitingRoom, waitingParticipantId, credentials, conferenceId, joinConference, navigate]);

  // Broadcast conference updates to other windows
  const broadcastUpdate = (type: string) => {
    try {
      const channel = new BroadcastChannel('video-conference-updates');
      channel.postMessage({ type, conferenceId });
      channel.close();
    } catch (err) {
      console.warn('BroadcastChannel not supported:', err);
    }
  };

  const handleEnd = async () => {
    try {
      await endConference({ variables: { id: conferenceId } });
      toast.success('Conference ended');
      broadcastUpdate('CONFERENCE_ENDED');
      setCallEnded(true);
    } catch (err: any) {
      toast.error(err.message || 'Failed to end conference');
      broadcastUpdate('CONFERENCE_ENDED');
      setCallEnded(true);
    }
  };

  const handleLeave = () => {
    broadcastUpdate('CONFERENCE_UPDATED');
    setCallEnded(true);
  };

  // Pre-meeting lobby screen (like Teams)
  if (showLobby && conference && !loading) {
    const buttonText = conference.status === 'SCHEDULED' 
      ? (canStartMeeting ? 'Start Meeting' : 'Join Meeting')
      : 'Join Meeting';

    return (
      <div className="min-h-screen bg-gray-900 flex flex-col">
        {/* Header */}
        <div className="bg-gray-800 px-6 py-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg p-1">
              <img src="/icon.png" alt="AALaw" className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-white">{conference.title}</h1>
              <p className="text-gray-400 text-sm">
                {conference.status === 'SCHEDULED' ? 'Ready to start' : 'Meeting in progress'}
              </p>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="max-w-4xl w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Video Preview */}
              <div className="relative">
                <div className="aspect-video bg-gray-800 rounded-2xl overflow-hidden relative">
                  {isCameraOn ? (
                    <video
                      ref={videoPreviewRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover transform scale-x-[-1]"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="w-24 h-24 bg-gray-700 rounded-full flex items-center justify-center">
                        <span className="text-4xl font-semibold text-white">
                          {user?.firstName?.charAt(0) || 'U'}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Camera/Mic status overlay */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-3">
                    <button
                      onClick={() => setIsMicOn(!isMicOn)}
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                        isMicOn ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-600 hover:bg-red-700'
                      }`}
                    >
                      {isMicOn ? (
                        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" clipRule="evenodd" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => setIsCameraOn(!isCameraOn)}
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                        isCameraOn ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-600 hover:bg-red-700'
                      }`}
                    >
                      {isCameraOn ? (
                        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Device status */}
                <div className="mt-4 flex items-center justify-center gap-4 text-sm">
                  <span className={`flex items-center gap-2 ${isMicOn ? 'text-green-400' : 'text-red-400'}`}>
                    <span className={`w-2 h-2 rounded-full ${isMicOn ? 'bg-green-400' : 'bg-red-400'}`}></span>
                    {isMicOn ? 'Mic on' : 'Mic off'}
                  </span>
                  <span className={`flex items-center gap-2 ${isCameraOn ? 'text-green-400' : 'text-red-400'}`}>
                    <span className={`w-2 h-2 rounded-full ${isCameraOn ? 'bg-green-400' : 'bg-red-400'}`}></span>
                    {isCameraOn ? 'Camera on' : 'Camera off'}
                  </span>
                </div>
              </div>

              {/* Settings Panel */}
              <div className="flex flex-col justify-center">
                <div className="bg-gray-800 rounded-2xl p-6">
                  <h2 className="text-lg font-semibold text-white mb-6">Ready to join?</h2>
                  
                  {/* Device selectors */}
                  <div className="space-y-4 mb-6">
                    {/* Microphone */}
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Microphone</label>
                      <select
                        value={selectedAudioDevice}
                        onChange={(e) => handleDeviceChange('audio', e.target.value)}
                        className="w-full bg-gray-700 text-white rounded-lg px-4 py-2.5 text-sm border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      >
                        {audioDevices.map(device => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Camera */}
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Camera</label>
                      <select
                        value={selectedVideoDevice}
                        onChange={(e) => handleDeviceChange('video', e.target.value)}
                        className="w-full bg-gray-700 text-white rounded-lg px-4 py-2.5 text-sm border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      >
                        {videoDevices.map(device => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Join/Start button */}
                  <Button
                    onClick={handleStartFromLobby}
                    className="w-full py-3 text-lg font-semibold bg-blue-600 hover:bg-blue-700"
                    disabled={!canStartMeeting && conference.status === 'SCHEDULED'}
                  >
                    {buttonText}
                  </Button>

                  {!canStartMeeting && conference.status === 'SCHEDULED' && (
                    <p className="text-yellow-400 text-sm mt-3 text-center">
                      Waiting for the host to start the meeting...
                    </p>
                  )}

                  {/* Cancel button */}
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/video-conferences')}
                    className="w-full mt-3 text-gray-400 hover:text-white"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading || isConnecting) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">{isConnecting ? 'Connecting to meeting...' : 'Loading conference...'}</p>
        </div>
      </div>
    );
  }

  // Waiting room screen
  if (isInWaitingRoom) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center max-w-md">
          <div className="bg-white rounded-lg p-1.5 mx-auto mb-6 w-fit">
            <img src="/icon.png" alt="AALaw" className="h-10 w-10" />
          </div>
          <div className="w-20 h-20 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-blue-400 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-white mb-2">Waiting Room</h1>
          <p className="text-gray-400 mb-4">
            {conference?.title || 'Video Conference'}
          </p>
          <p className="text-gray-500 mb-6">
            Please wait for the host to admit you to the meeting.
          </p>
          <div className="flex items-center justify-center gap-2 text-gray-500 mb-6">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
          
          <Button
            variant="outline"
            onClick={() => {
              setIsInWaitingRoom(false);
              navigate('/video-conferences');
            }}
            className="text-gray-300 border-gray-600 hover:bg-gray-800"
          >
            Leave Waiting Room
          </Button>
        </div>
      </div>
    );
  }

  if (error || !conference) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error?.message || 'Conference not found'}</p>
          <button
            onClick={() => navigate('/video-conferences')}
            className="px-4 py-2 bg-white text-gray-900 rounded-lg hover:bg-gray-100"
          >
            Back to Conferences
          </button>
        </div>
      </div>
    );
  }

  if (conference.status === 'CANCELLED') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <p className="text-yellow-400 mb-4">This conference has been cancelled</p>
          <button
            onClick={() => navigate('/video-conferences')}
            className="px-4 py-2 bg-white text-gray-900 rounded-lg hover:bg-gray-100"
          >
            Back to Conferences
          </button>
        </div>
      </div>
    );
  }

  if (conference.status === 'COMPLETED') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <p className="text-gray-400 mb-4">This conference has ended</p>
          <button
            onClick={() => navigate('/video-conferences')}
            className="px-4 py-2 bg-white text-gray-900 rounded-lg hover:bg-gray-100"
          >
            Back to Conferences
          </button>
        </div>
      </div>
    );
  }

  // Call ended screen
  if (callEnded) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          {/* Logo */}
          <div className="bg-white rounded-lg p-1.5 mx-auto mb-6 w-fit">
            <img src="/icon.png" alt="AALaw" className="h-10 w-10" />
          </div>
          
          {/* Icon */}
          <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 sm:w-12 sm:h-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          {/* Title */}
          <h1 className="text-2xl sm:text-3xl font-semibold text-white mb-3">This conference has ended</h1>
          
          {/* Recording info */}
          {conference?.isRecordingEnabled && (
            <div className="bg-gray-800 rounded-lg p-4 mb-6 mx-2">
              <div className="flex items-center justify-center gap-2 text-gray-300">
                <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span className="text-sm">Recording will be available shortly</span>
              </div>
            </div>
          )}
          
          {/* Back to Conferences button for staff */}
          <Button
            onClick={() => navigate('/video-conferences')}
            className="w-full bg-white text-gray-900 hover:bg-gray-100 py-3 text-lg font-medium"
          >
            Back to Conferences
          </Button>
        </div>
      </div>
    );
  }

  if (!credentials) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">Preparing meeting...</p>
        </div>
      </div>
    );
  }

  return (
    <VideoCallWrapper
      credentials={credentials}
      conferenceId={conferenceId!}
      conferenceTitle={conference.title}
      isHost={isHost}
      isRecordingEnabled={conference.isRecordingEnabled}
      currentUserId={user?.id || ''}
      currentUserName={user ? `${user.firstName} ${user.lastName}` : 'Unknown'}
      currentUserRole={user?.role || 'CLIENT'}
      waitingRoomEnabled={conference.waitingRoomEnabled ?? true}
      initialWaitingParticipants={(conference.waitingRoomParticipants || []).map(p => ({
        ...p,
        participantType: p.participantType as 'CLIENT' | 'INQUIRY' | 'GUEST' | 'STAFF',
      }))}
      canEndMeeting={isHost || didStartMeeting}
      onEndCall={handleEnd}
      onLeaveCall={handleLeave}
    />
  );
}
